import React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fieldMatchesAllowedType, filterQueryFields, QueryField, QueryInfo } from "system/query/QueryInfo";

export interface QueryFieldsSelectorProps {
  queryInfo: QueryInfo;
  allowedFieldTypes: string[];

  // If true, restrict multi-value selection to a single type from
  // the allowedFieldTypes list based on the first selected field type
  restrictToSingleType?: boolean;

  selectedFieldIds: string[];
  setSelectedFieldIds: Function;

  // Only show fields that have the query_supported flag set to true
  requireQuerySupported?: boolean;

  // Only show fields that have the agg_supported flag set to true
  requireAggSupported?: boolean;

  isInline?: boolean;

  children?: ReactNode;

  variant?: "standard" | "outlined" | "filled";

  placeholder? : string | undefined;

  size?: "small" | "medium" | "large";
}

export function QueryFieldsSelector(props: QueryFieldsSelectorProps): JSX.Element {
  const { i18n } = useTranslation();

  const validFields = useMemo(() => {
    let allowedFieldTypes = props.allowedFieldTypes;
    if (props.restrictToSingleType && props.selectedFieldIds.length > 0) {
      const firstSelectedField = props.queryInfo?.fields?.find(field => field.id === props.selectedFieldIds[0]);
      if (firstSelectedField) {
        allowedFieldTypes = props.allowedFieldTypes.filter((type) => fieldMatchesAllowedType(firstSelectedField, type));
      }
    }

    const allFields = props.queryInfo?.fields ?? [];
    const filteredFields = filterQueryFields( allFields, {
      allowedFieldTypes: allowedFieldTypes,
      requireQuerySupported: props.requireQuerySupported,
      requireAggSupported: props.requireAggSupported
    });

    return filteredFields.sort((fieldA, fieldB) => {
      if (fieldA.label.toLowerCase() > fieldB.label.toLowerCase()) return 1;
      if (fieldA.label.toLowerCase() < fieldB.label.toLowerCase()) return -1;
      return 0;
    });
  }, [props.queryInfo, props.allowedFieldTypes, props.requireAggSupported, props.requireQuerySupported, props.restrictToSingleType, props.selectedFieldIds]);

  return (
    <Box sx={{ display: props.isInline ? "inline-block" : "block", verticalAlign:"top", p:0, m:0, mr: props.isInline ? 1 : 0 }}>
      {props.queryInfo && validFields.length === 0 && 
        <Box sx={{p:1, fontSize:"1.1rem"}}>{ i18n.t("query-form.no-valid-fields") }</Box>
      }
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={validFields}
        className="drag-cancel"
        size={props.size || "medium"}
        defaultValue={[]}
        value={validFields.filter(field => props.selectedFieldIds.includes(field.id)) || []}
        onChange={(event: any, newValue: QueryField[] | null) => {
          if (newValue) {
            props.setSelectedFieldIds(newValue.map(field => field.id));
          }
        }}
        sx={{minWidth: 220}}
        renderInput={(params) => <TextField autoComplete="off" {...params} variant={props.variant} placeholder={props.selectedFieldIds?.length > 0 ? undefined : props.placeholder} />}
      />
    </Box>
  );
}